/**
 * useConversationCases — la liste des dossiers d'une conversation, vue depuis le
 * composeur (spec #2592) : celle du serveur, les gestes de l'avocat qui partiront
 * avec son prochain message, et le dossier du composeur qui suit le principal.
 *
 * Le serveur tient la liste. L'écran la reçoit par la trame `conversation_cases`
 * (à chaque tour accepté et à chaque changement) et par le détail de la
 * conversation à la réouverture ; un refus n'envoie aucune trame, d'où
 * `reprendreApresRefus`.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ConversationCaseListSchema,
  type ConversationCaseGesture,
  type ConversationCaseListItem,
  type ConversationCaseRefusal,
} from '@hector/shared-types';
import { useConversation } from '@/hooks/useConversationsAPI';
import { useActiveSessionsStore } from '../stores/activeSessionsStore';
import {
  connexesAffiches,
  gestesAEnvoyer,
  useConversationCasesStore,
} from '../stores/conversationCasesStore';

const AUCUN_GESTE: readonly ConversationCaseGesture[] = [];

function principalDe(liste: readonly ConversationCaseListItem[] | null): ConversationCaseListItem | null {
  return liste?.find((item) => item.role === 'principal') ?? null;
}

/** Les refus qui portent sur le dossier du composeur, le seul cas où il doit changer. */
function toucheLeComposeur(refus: ConversationCaseRefusal): boolean {
  return refus.code === 'principal_change_refused'
    || refus.code === 'case_access_denied'
    || refus.code === 'principal_case_unreadable'
    || (refus.code === 'case_change_refused' && refus.caseChange === 'remove_principal');
}

interface UseConversationCasesInput {
  conversationId: string | null;
  /** Le composeur vierge de `/assistant` et celui du dock ont chacun leurs gestes. */
  hote: 'url' | 'host';
  /** Le dossier de la page qui héberge le dock : deux dossiers n'ont pas le même brouillon. */
  hostCaseId: string | null;
  /** Le dossier du composeur : le `case_id` du prochain message. */
  selectedCaseId: string | null;
  onCaseSelect: (caseId: string | null) => void;
  /** Faux pour le chat verrouillé sur son dossier : il ne gère aucune liste. */
  enabled: boolean;
  /** Faux dans le chat d'un dossier, dont le composeur garde toujours un dossier. */
  allowsCaseClear: boolean;
  /** Titre d'un dossier encore absent de la liste du serveur. */
  titreDe: (caseId: string) => string | undefined;
}

/** Voir le module. */
export function useConversationCases({
  conversationId,
  hote,
  hostCaseId,
  selectedCaseId,
  onCaseSelect,
  enabled,
  allowsCaseClear,
  titreDe,
}: UseConversationCasesInput) {
  // Une conversation pas encore créée a aussi ses gestes. Ils attendent sous la clé
  // de son hôte, pas d'une instance du composant : `AgentV3View` remonte le composeur
  // à chaque identité de route, et un premier message refusé doit les retrouver.
  const cleBrouillon = `brouillon:${hote}:${hostCaseId ?? 'sans-dossier'}`;
  const cle = conversationId ?? cleBrouillon;

  // Tant que le serveur n'a pas confirmé la conversation, son détail répondrait 404.
  const enAttente = useActiveSessionsStore((state) => (
    conversationId ? state.pendingConversations[conversationId] !== undefined : false
  ));
  // Même clé de requête que `AgentV3View` : pas de seconde lecture du détail.
  const { data: detail, dataUpdatedAt: detailLuLe, refetch: relireLeDetail } = useConversation(conversationId ?? '', {
    enabled: enabled && conversationId !== null && !enAttente,
  });
  const listeVive = useConversationCasesStore((state) => (
    conversationId ? state.listes[conversationId] : undefined
  ));
  const gestes = useConversationCasesStore((state) => state.gestes[cle] ?? AUCUN_GESTE);
  const gestesEnVol = useConversationCasesStore((state) => state.enVol[cle] !== undefined);

  const liste = useMemo(() => {
    if (!enabled) return null;
    const lue = ConversationCaseListSchema.safeParse(detail?.conversation_cases);
    if (!lue.success) return listeVive?.cases ?? null;
    // Le plus récent des deux : une relecture du détail vaut mieux qu'une vieille trame.
    return listeVive && listeVive.recuLe > detailLuLe ? listeVive.cases : lue.data;
  }, [detail?.conversation_cases, detailLuLe, enabled, listeVive]);

  const principal = principalDe(liste);
  const principalCaseId = principal?.case_id ?? null;

  const retraitDuPrincipalEnAttente = gestes.some((geste) => geste.kind === 'remove_principal');
  const principalSuivi = useRef<{ conversationId: string | null; caseId: string | null } | null>(null);
  useEffect(() => {
    if (!liste) return;
    const precedent = principalSuivi.current?.conversationId === conversationId
      ? principalSuivi.current.caseId
      : undefined;
    principalSuivi.current = { conversationId, caseId: principalCaseId };
    if (precedent === undefined) {
      // Première vue de cette liste : l'hôte a déjà posé le dossier du détail. Seul
      // un composeur vide la suit, sinon l'avocat qui a fait un autre choix le perdrait.
      if (principalCaseId && selectedCaseId === null && !retraitDuPrincipalEnAttente) onCaseSelect(principalCaseId);
      return;
    }
    if (precedent === principalCaseId) return;
    // Le composeur ne suit que s'il montrait le principal : un autre dossier choisi
    // et pas encore envoyé reste le choix de l'avocat. Sans ce suivi, un composeur
    // resté sur l'ancien principal le rétablirait au message suivant.
    if (selectedCaseId === precedent) onCaseSelect(principalCaseId);
  }, [conversationId, liste, onCaseSelect, principalCaseId, retraitDuPrincipalEnAttente, selectedCaseId]);

  const store = useConversationCasesStore.getState;
  const detailRoleRef = useRef(detail?.my_role);
  detailRoleRef.current = detail?.my_role;

  // Un geste devenu sans objet ne doit pas rester en attente : une pièce d'un connexe
  // ouverte dans le canvas y laisserait un ajout, et retirer ce connexe annulerait cet
  // ajout au lieu de produire un retrait.
  // Pas pendant qu'un message part : la liste ne dit pas encore ce qu'il va changer.
  useEffect(() => {
    if (!liste || gestesEnVol) return;
    const utiles = gestesAEnvoyer(gestes, liste);
    if (utiles.length !== gestes.length) store().garder(cle, utiles);
  }, [cle, gestes, gestesEnVol, liste, store]);

  /**
   * Le choix dans le composeur. Choisir un dossier reste le geste actuel : il part
   * comme `case_id` du message. Vider le composeur est un vrai retrait quand il
   * montrait le principal : un message sans dossier ne retire rien côté serveur.
   */
  const choisirDossier = useCallback((caseId: string | null) => {
    if (detailRoleRef.current === 'viewer') return;
    if (caseId !== null) {
      store().annulerRetraitDuPrincipal(cle, caseId);
      onCaseSelect(caseId);
      return;
    }
    if (principalCaseId && selectedCaseId !== principalCaseId) {
      // Il abandonne un choix pas encore envoyé : le principal reste, et le montrer vide serait faux.
      onCaseSelect(principalCaseId);
      return;
    }
    if (principalCaseId) store().retirerPrincipal(cle, principalCaseId);
    onCaseSelect(null);
  }, [cle, onCaseSelect, principalCaseId, selectedCaseId, store]);

  // Le rôle d'à présent, pas celui du rendu qui a monté le bouton : un lecteur
  // n'écrit aucun geste, même depuis un rappel resté accroché à l'écran.
  const lecteur = detail?.my_role === 'viewer';
  const ajouterConnexe = useCallback((caseId: string) => {
    if (!lecteur) store().ajouter(cle, caseId);
  }, [cle, lecteur, store]);
  const retirerConnexe = useCallback((caseId: string) => {
    if (lecteur) return;
    // Le geste suit le rôle que le SERVEUR donne au dossier : l'ancien principal montré
    // en connexe parce que l'avocat en a choisi un autre reste son principal jusqu'à l'envoi.
    if (caseId === principalCaseId) store().retirerPrincipal(cle, caseId);
    else store().retirerConnexe(cle, caseId);
  }, [cle, lecteur, principalCaseId, store]);

  const connexes = useMemo(
    () => connexesAffiches({ liste, gestes, principalCaseId: selectedCaseId, titreDe }),
    [gestes, liste, selectedCaseId, titreDe],
  );

  /**
   * Après un refus : le geste refusé est oublié, et la liste relue au détail
   * (aucune trame n'accompagne un refus).
   *
   * Le composeur ne revient au principal que si c'est lui que le refus touche. Pour
   * un autre geste refusé, il garde le choix de l'avocat. Et il ne remonte jamais un
   * principal dont le retrait reste en attente : le message renvoyé l'annulerait.
   *
   * @param input.conversationCible - La conversation du message refusé, lue en base.
   *   `null` pour un premier message : la conversation n'existe plus, rien à relire.
   * @param input.estEncoreAffichee - Relue après la lecture réseau : l'avocat a pu changer de fil.
   */
  const reprendreApresRefus = useCallback(async (input: {
    cleDesGestes: string;
    refus: ConversationCaseRefusal;
    conversationCible: string | null;
    estEncoreAffichee: () => boolean;
  }) => {
    const { refus, cleDesGestes, conversationCible } = input;
    store().oublierApresRefus(cleDesGestes, refus);
    if (conversationCible === null) return;
    store().oublierListe(conversationCible);
    const { data } = await relireLeDetail();
    if (!data || !input.estEncoreAffichee() || !toucheLeComposeur(refus)) return;
    const lue = ConversationCaseListSchema.safeParse(data.conversation_cases);
    if (!lue.success) return;
    const relu = principalDe(lue.data)?.case_id ?? null;
    const retire = (store().gestes[cleDesGestes] ?? []).some((geste) => (
      geste.kind === 'remove_principal' && geste.case_id === relu
    ));
    const cible = retire ? null : relu;
    if (cible !== null || allowsCaseClear) onCaseSelect(cible);
  }, [allowsCaseClear, onCaseSelect, relireLeDetail, store]);

  const lireTour = useCallback((nouvelleConversation: boolean) => {
    // Une nouvelle conversation forcée part du brouillon : la liste de ce rendu est
    // encore celle du fil quitté, elle ne dit rien du message.
    const cleDesGestes = nouvelleConversation ? cleBrouillon : cle;
    const listeDuTour = nouvelleConversation ? null : liste;
    return {
      cleDesGestes,
      gestes: enabled && !lecteur
        ? gestesAEnvoyer(store().gestes[cleDesGestes] ?? AUCUN_GESTE, listeDuTour)
        : [],
      /** `null` tant que l'écran n'a aucune liste de ce fil : le dossier part alors avec le message. */
      principalServeur: listeDuTour ? principalDe(listeDuTour)?.case_id ?? null : null,
    };
  }, [cle, cleBrouillon, enabled, lecteur, liste, store]);

  return {
    liste,
    principal,
    connexes,
    lecteur,
    choisirDossier,
    ajouterConnexe,
    retirerConnexe,
    /** Ce qu'emporte un message, figé à l'envoi. */
    lireTour,
    marquerGestesEnVol: useCallback(
      (cleDesGestes: string, envoyes: readonly ConversationCaseGesture[]) => store().marquerEnVol(cleDesGestes, envoyes),
      [store],
    ),
    terminerGestesEnVol: useCallback((cleDesGestes: string) => store().terminerEnVol(cleDesGestes), [store]),
    consommerGestes: useCallback(
      (cleDesGestes: string, envoyes: readonly ConversationCaseGesture[]) => store().consommer(cleDesGestes, envoyes),
      [store],
    ),
    reprendreApresRefus,
  };
}
